import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import Image from 'next/image'

import Container from '@/components/base/Container'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import { MediaQuery } from '@/constants'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type MediaProps = HTMLAttributes<HTMLDivElement> & {
  poster?: WPImage
  poster_mobile?: WPImage
  video?: WPVideo
  video_mobile?: WPVideo
}

const Media = ({
  className,
  poster,
  poster_mobile,
  video,
  video_mobile
}: MediaProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const isMobile = useMedia(MediaQuery.SMALL, true)
  const player = useRef<Plyr>(null!)
  const videoRef = useRef<HTMLVideoElement>(null!)
  const timeoutID = useRef<NodeJS.Timeout | null>(null)
  const hasVideo = useMemo(
    () => video?.url || video_mobile?.url,
    [video, video_mobile]
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('plyr').then((PlyrModule) => {
        player.current = new PlyrModule.default(videoRef.current, {})

        player.current.on('playing', () => {
          setIsPlaying(true)

          if (timeoutID.current) {
            clearTimeout(timeoutID.current)
          }
        })

        player.current.on('pause', () => {
          timeoutID.current = setTimeout(() => {
            setIsPlaying(false)
          }, 500)
        })
      })
    }
  }, [])

  return (
    <Motion animation={opacityReveal}>
      <div
        className={cn(
          'bg-text-primary-invert-dark relative aspect-video',
          className
        )}
        style={{ '--plyr-color-main': '#1B3765' } as React.CSSProperties}
        data-media
      >
        <Container className="relative h-full w-full object-cover">
          {hasVideo ? (
            <div className="relative h-full w-full object-cover">
              {poster?.url && (
                <Image
                  src={
                    isMobile && poster_mobile?.url
                      ? poster_mobile.url
                      : poster.url
                  }
                  width={poster.width}
                  height={poster.height}
                  draggable={false}
                  alt={
                    isMobile && poster_mobile?.alt
                      ? poster_mobile.alt
                      : poster.alt || 'Poster'
                  }
                  className={cn(
                    'absolute left-0 top-0 z-20 h-full w-full cursor-pointer object-cover',
                    'user-select-none pointer-events-none',
                    'transition-opacity duration-1000',
                    isPlaying ? 'opacity-0' : 'opacity-100'
                  )}
                  style={{ transition: 'opacity 0.3s' }}
                  priority={true}
                  fetchPriority="high"
                  loading="eager"
                />
              )}
              <button
                type="button"
                aria-label="Play video"
                disabled={isPlaying}
                tabIndex={isPlaying ? -1 : 0}
                className={cn(
                  'relative h-full w-full',
                  'absolute left-0 top-0 z-30',
                  'bg-surface-primary-dark/30 flex flex-col items-center justify-center',
                  'transition-opacity duration-1000',
                  isPlaying
                    ? 'pointer-events-none opacity-0'
                    : 'pointer-events-auto opacity-100'
                )}
                onClick={() => {
                  videoRef.current.play()
                }}
              >
                <span className="gap-spacing-sm flex flex-col items-center justify-center">
                  <SVGIcon
                    symbol="play-icon"
                    className={cn('fill-current', 'size-[40px]')}
                  />
                </span>
              </button>
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                data-poster={
                  isMobile && poster_mobile?.url
                    ? poster_mobile.url
                    : poster?.url
                }
              >
                <source
                  src={
                    isMobile && video_mobile?.url
                      ? video_mobile.url
                      : video?.url
                  }
                  type="video/mp4"
                />
              </video>
            </div>
          ) : (
            (poster?.url || poster_mobile?.url) && (
              <>
                <Image
                  src={
                    isMobile && poster_mobile?.url
                      ? poster_mobile.url
                      : poster?.url || ''
                  }
                  width={
                    isMobile && poster_mobile?.url
                      ? poster_mobile.width
                      : poster?.width
                  }
                  height={
                    isMobile && poster_mobile?.url
                      ? poster_mobile.height
                      : poster?.height
                  }
                  alt={
                    isMobile && poster_mobile?.alt
                      ? poster_mobile.alt
                      : poster?.alt || 'Poster'
                  }
                  className="user-select-none pointer-events-none h-auto w-full"
                  fetchPriority="high"
                  loading="eager"
                />
              </>
            )
          )}
        </Container>
      </div>
    </Motion>
  )
}

export default Media
